/**
 * CloudWatch Logs Service for Upgrade Medic
 * Fetches job execution logs from CloudWatch using cross-account access
 */

import { CloudWatchLogsClient, GetLogEventsCommand, FilterLogEventsCommand, OutputLogEvent, FilteredLogEvent } from '@aws-sdk/client-cloudwatch-logs';
import { STSClient, AssumeRoleCommand } from '@aws-sdk/client-sts';
import { getStageConfig } from 'src/components/fintech-ops/upgrade-medic/config';
import { fetchAuthSession } from 'aws-amplify/auth';

// Log event structure from CloudWatch
export interface CloudWatchLogEvent {
  timestamp: number;
  level: string;
  eventType: string;
  data: {
    type: string;
    jobId: string;
    progress?: number;
    message?: string;
    stage?: string;
    pipelineId?: string;
    [key: string]: any;
  };
  loggedAt: string;
}

// Parsed log entry for UI
export interface ParsedLogEntry {
  id: string;
  timestamp: Date;
  level: 'info' | 'warning' | 'error' | 'agent' | 'phase' | 'tool';
  eventType: string;
  message: string;
  stage?: string;
  pipelineId?: string;
  progress?: number;
  raw?: any;
}

export class CloudWatchLogsService {
  private static client: CloudWatchLogsClient | null = null;
  private static credentialsExpiry: number = 0;

  /**
   * Get or create CloudWatch Logs client with cross-account credentials via STS AssumeRole
   */
  private static async getClient(): Promise<CloudWatchLogsClient> {
    // Check if we need to refresh credentials (expired or will expire in 5 minutes)
    const now = Date.now();
    if (this.client && this.credentialsExpiry > now + 5 * 60 * 1000) {
      return this.client;
    }

    const config = getStageConfig();

    console.log(`[CloudWatchLogsService] Creating cross-account client for ${config.cloudWatch.logGroupName} using role ${config.roleArn}`);

    try {
      // Get Cognito credentials from Amplify auth session
      const session = await fetchAuthSession();
      const cognitoCredentials = session.credentials;

      if (!cognitoCredentials) {
        throw new Error('No credentials available from Cognito');
      }

      // Create STS client with Cognito credentials
      const stsClient = new STSClient({
        region: config.region,
        credentials: {
          accessKeyId: cognitoCredentials.accessKeyId,
          secretAccessKey: cognitoCredentials.secretAccessKey,
          sessionToken: cognitoCredentials.sessionToken
        }
      });

      // Assume role in the Upgrade Medic account (same role used for DynamoDB)
      const assumeRoleCommand = new AssumeRoleCommand({
        RoleArn: config.roleArn,
        RoleSessionName: 'UpgradeMedicCloudWatchLogsSession',
        DurationSeconds: 3600 // 1 hour
      });

      const roleCredentials = await stsClient.send(assumeRoleCommand);

      if (!roleCredentials.Credentials) {
        throw new Error('Failed to assume cross-account role');
      }

      // Create CloudWatch Logs client with assumed role credentials
      this.client = new CloudWatchLogsClient({
        region: config.region,
        credentials: {
          accessKeyId: roleCredentials.Credentials.AccessKeyId!,
          secretAccessKey: roleCredentials.Credentials.SecretAccessKey!,
          sessionToken: roleCredentials.Credentials.SessionToken
        }
      });

      // Track credential expiry
      this.credentialsExpiry = roleCredentials.Credentials.Expiration?.getTime() || now + 3600 * 1000;

      console.log(`[CloudWatchLogsService] Cross-account client created successfully`);
      return this.client;
    } catch (error) {
      console.error('[CloudWatchLogsService] Failed to create cross-account client:', error);
      throw error;
    }
  }

  // getStage and getConfig removed — use getStageConfig() directly

  /**
   * Fetch logs for a specific job ID
   * @param jobId - The job ID (also the log stream name)
   * @param nextToken - Token for pagination
   * @param limit - Max number of events to fetch
   */
  public static async fetchJobLogs(jobId: string, nextToken?: string, limit: number = 500): Promise<{ logs: ParsedLogEntry[]; nextToken?: string }> {
    const stageConfig = getStageConfig();
    console.log(`[CloudWatchLogsService] Fetching logs for job: ${jobId} from ${stageConfig.cloudWatch.logGroupName}`);

    try {
      const client = await this.getClient();

      const command = new GetLogEventsCommand({
        logGroupName: stageConfig.cloudWatch.logGroupName,
        logStreamName: jobId,
        startFromHead: true,
        limit,
        nextToken
      });

      const response = await client.send(command);

      // Parse log events and filter out empty/skipped entries
      const logs: ParsedLogEntry[] = (response.events || [])
        .map((event: OutputLogEvent, index: number) => {
          return this.parseLogEvent(event.message || '', event.timestamp || Date.now(), index);
        })
        .filter((log: ParsedLogEntry) => log.message && log.message.trim().length > 0);

      console.log(`[CloudWatchLogsService] Fetched ${logs.length} meaningful log events (filtered from ${response.events?.length || 0} total)`);

      return {
        logs,
        nextToken: response.nextForwardToken
      };
    } catch (error: any) {
      // Handle stream not found (job hasn't started logging yet)
      if (error.name === 'ResourceNotFoundException') {
        console.log(`[CloudWatchLogsService] Log stream not found for job: ${jobId}`);
        return { logs: [] };
      }
      console.error('[CloudWatchLogsService] Failed to fetch logs:', error);
      throw error;
    }
  }

  /**
   * Fetch ALL logs for a specific job by paginating through all pages.
   * Uses GetLogEventsCommand with startFromHead and follows nextForwardToken
   * until no new events are returned.
   * @param jobId - The job ID (also the log stream name)
   */
  public static async fetchAllJobLogs(jobId: string): Promise<ParsedLogEntry[]> {
    const allLogs: ParsedLogEntry[] = [];
    let nextToken: string | undefined;
    let previousToken: string | undefined;

    do {
      const result = await this.fetchJobLogs(jobId, nextToken, 500);
      allLogs.push(...result.logs);

      previousToken = nextToken;
      nextToken = result.nextToken;

      // GetLogEventsCommand returns the same token when there are no more events
      if (nextToken === previousToken) {
        break;
      }
    } while (nextToken);

    console.log(`[CloudWatchLogsService] Fetched ALL logs for job ${jobId}: ${allLogs.length} total entries`);
    return allLogs;
  }

  /**
   * Search logs with a filter pattern
   * @param jobId - The job ID to search within
   * @param filterPattern - CloudWatch filter pattern
   */
  public static async searchLogs(jobId: string, filterPattern: string, startTime?: Date, endTime?: Date): Promise<ParsedLogEntry[]> {
    const stageConfig = getStageConfig();

    try {
      const client = await this.getClient();

      const command = new FilterLogEventsCommand({
        logGroupName: stageConfig.cloudWatch.logGroupName,
        logStreamNames: [jobId],
        filterPattern,
        startTime: startTime?.getTime(),
        endTime: endTime?.getTime(),
        limit: 500
      });

      const response = await client.send(command);

      return (response.events || []).map((event: FilteredLogEvent, index: number) => {
        return this.parseLogEvent(event.message || '', event.timestamp || Date.now(), index);
      });
    } catch (error) {
      console.error('[CloudWatchLogsService] Failed to search logs:', error);
      return [];
    }
  }

  /**
   * Parse a raw CloudWatch log message into a structured log entry
   */
  private static parseLogEvent(message: string, timestamp: number, index: number): ParsedLogEntry {
    try {
      // Try to parse as JSON - CloudWatch logs may be JSON or plain text
      const parsed = JSON.parse(message);

      // Handle different log formats
      const eventType = parsed.type || parsed.eventType || 'unknown';
      const data = parsed.data || parsed;

      // Map event types to UI log levels
      const levelMap: Record<string, ParsedLogEntry['level']> = {
        ERROR: 'error',
        WARN: 'warning',
        WARNING: 'warning',
        INFO: 'info',
        DEBUG: 'agent',
        error: 'error',
        job_failed: 'error',
        stage_update: 'phase',
        stage_start: 'phase',
        stage_end: 'phase',
        agent_tool_start: 'tool',
        agent_tool_result: 'tool',
        agent_text: 'agent',
        react_step: 'agent',
        progress: 'info',
        job_started: 'phase',
        job_completed: 'phase',
        pipeline_completed: 'phase',
        pipeline_started: 'phase',
        summary: 'phase'
      };

      const level = levelMap[parsed.level] || levelMap[eventType] || 'info';

      // Build message from event data based on event type
      let displayMessage = '';
      let shouldSkip = false;

      switch (eventType) {
        case 'agent_text':
          // Skip very short fragments (streaming chunks) - they clutter the log
          const textContent = parsed.text || data.text || '';
          if (textContent.length < 20 && !data.formatted) {
            shouldSkip = true;
          }
          displayMessage = data.formatted || textContent;
          break;

        case 'react_step':
          // ReAct pattern: Thought/Action/Observation
          const stepData = data.data || data;
          const stepType = stepData.stepType || 'thought';
          const stepEmoji = stepData.emoji || (stepType === 'thought' ? '💭' : stepType === 'action' ? '🎬' : '👀');
          const stepLabel = stepType.charAt(0).toUpperCase() + stepType.slice(1);
          displayMessage = data.formatted || `${stepEmoji} **${stepLabel}:** ${stepData.content || ''}`;
          break;

        case 'stage_update':
        case 'stage_start':
          displayMessage = data.formatted || `Stage: ${data.stage || parsed.stage} - ${data.message || data.stageMessage || 'Processing...'}`;
          break;

        case 'stage_end':
          if (data.formatted) {
            displayMessage = data.formatted;
          } else if (data.success !== false) {
            displayMessage = `✅ Stage: ${data.stage || parsed.stage} - Completed`;
          } else {
            displayMessage = `❌ Stage: ${data.stage || parsed.stage} - Failed: ${data.error || 'Unknown error'}`;
          }
          break;

        case 'agent_tool_start':
          const toolName = data.tool || parsed.tool || 'unknown';
          const toolCount = data.count || parsed.count || '';
          const toolDisplay = data.display || data.args || '';
          displayMessage = data.formatted || `↘ [Tool #${toolCount}] ${toolName}: ${toolDisplay}`;
          break;

        case 'agent_tool_result':
          // Skip tool results - too verbose
          shouldSkip = true;
          break;

        case 'progress':
          displayMessage = data.formatted || `[${data.progress || parsed.progress || 0}%] ${data.message || parsed.message || ''}`;
          break;

        case 'job_started':
          displayMessage = data.formatted || `🚀 Job started: ${data.jobId || parsed.jobId}`;
          break;

        case 'job_completed':
          const summary = data.summary || {};
          displayMessage = data.formatted || `✅ Job completed: ${summary.success || 0}/${summary.total || 0} succeeded`;
          break;

        case 'job_failed':
          displayMessage = data.formatted || `❌ Job failed: ${data.error || parsed.error || data.message || parsed.message || 'Unknown error'}`;
          break;

        case 'pipeline_started':
          displayMessage = data.formatted || `▶️ Pipeline started: ${data.pipelineId || parsed.pipelineId}`;
          break;

        case 'pipeline_completed':
          displayMessage = data.formatted || `✅ Pipeline completed: ${data.pipelineId || parsed.pipelineId}`;
          break;

        case 'pipeline_failed':
          displayMessage = data.formatted || `❌ Pipeline failed: ${data.pipelineId || parsed.pipelineId} - ${data.error || 'Unknown error'}`;
          break;

        case 'artifact_created':
          displayMessage = data.formatted || `🔗 ${data.artifactType || 'Artifact'}: ${data.artifactTitle || data.artifactUrl || ''}`;
          break;

        case 'summary':
          // Rich summary - use formatted content
          displayMessage = data.formatted || data.data?.formatted || data.display || data.message || `📋 Summary: Workflow completed`;
          break;

        case 'log':
          // Generic log event — prefer formatted over display over message
          displayMessage = data.formatted || data.display || data.message || parsed.message || '';
          break;

        default:
          // Fallback: prefer formatted over display over message
          displayMessage = data.formatted || data.display || data.message || parsed.message || data.text || parsed.text || '';
          if (!displayMessage) {
            // Last resort: stringify but try to make it readable
            displayMessage = JSON.stringify(data).substring(0, 200);
          }
      }

      // Skip entries marked for skipping (streaming fragments, verbose results)
      if (shouldSkip) {
        return {
          id: `log-${timestamp}-${index}-skip`,
          timestamp: new Date(parsed.timestamp || timestamp),
          level: 'agent',
          eventType,
          message: '', // Empty message will be filtered out
          stage: data.stage,
          pipelineId: data.pipelineId || parsed.pipelineId,
          progress: data.progress,
          raw: parsed
        };
      }

      return {
        id: `log-${timestamp}-${index}`,
        timestamp: new Date(parsed.timestamp || timestamp),
        level,
        eventType,
        message: displayMessage,
        stage: data.stage,
        pipelineId: data.pipelineId || parsed.pipelineId,
        progress: data.progress,
        raw: parsed
      };
    } catch {
      // If not JSON, return as plain text
      return {
        id: `log-${timestamp}-${index}`,
        timestamp: new Date(timestamp),
        level: 'agent',
        eventType: 'raw',
        message: message
      };
    }
  }

  /**
   * Start polling for new logs (for live updates during job execution)
   * Returns a cleanup function to stop polling
   */
  public static startLogPolling(jobId: string, onNewLogs: (logs: ParsedLogEntry[]) => void, intervalMs: number = 2000): () => void {
    let lastToken: string | undefined;
    let isPolling = true;

    const poll = async () => {
      if (!isPolling) return;

      try {
        const { logs, nextToken } = await this.fetchJobLogs(jobId, lastToken, 100);

        if (logs.length > 0) {
          onNewLogs(logs);
        }

        lastToken = nextToken;
      } catch (error) {
        console.error('[CloudWatchLogsService] Polling error:', error);
      }

      if (isPolling) {
        setTimeout(poll, intervalMs);
      }
    };

    // Start polling
    poll();

    // Return cleanup function
    return () => {
      isPolling = false;
    };
  }
}
